import { ImagePlaceholder } from "shared/components/atoms";
import { StopBlock, OperatorRecord } from "../../types";
import { i18nLanguage } from "i18n";
import { getLocalizedString } from "../../utils/getLocalizedString";
import { getCloudinaryImageUrl } from "../../utils/getCloudinaryImageUrl";
import { normalizeRichTextHtml } from "../../utils/normalizeRichTextHtml";
import { truncateRichTextHtml } from "../../utils/truncateRichTextHtml";
import { formatPhoneNumber } from "shared/utils";

const DESCRIPTION_MAX_LENGTH = 300;

const READ_MORE_LABEL: Record<i18nLanguage, string> = {
  en: "Read more",
  fr: "Lire la suite",
  es: "Leer más",
};

type TripperStopCardProps = {
  item: StopBlock;
  operatorByEntityId: Record<string, NonNullable<OperatorRecord>>;
  language: i18nLanguage;
  markerNumber: number;
  isActive: boolean;
  hideTag?: boolean;
  onNameClick?: (stop: StopBlock, operator: OperatorRecord, markerNumber: number) => void;
  onOutboundClick?: (operatorEntityId: string, linkType: "google_maps" | "website") => void;
};

const formatAddress = (
  address?: {
    line1?: string | null;
    city?: string | null;
    subdivision?: string | null;
  } | null,
) => {
  const parts = [address?.line1, address?.city, address?.subdivision].filter(Boolean);
  return parts.join(", ");
};

export const TripperStopCard = ({
  item,
  operatorByEntityId,
  language,
  markerNumber,
  isActive,
  hideTag,
  onNameClick,
  onOutboundClick,
}: TripperStopCardProps) => {
  const operatorEntityId = item.entityRef?.entityId?.toString();
  const operator = operatorEntityId ? operatorByEntityId[operatorEntityId] : null;
  const operatorName = operator?.name
    ? getLocalizedString(operator.name, language)
    : null;
  const tagline = item.title
    ? getLocalizedString(item.title, language)
    : null;
  const customDescription = item.notes ? getLocalizedString(item.notes, language).trim() : "";
  const fallbackDescription = operator?.description
    ? getLocalizedString(operator.description, language)
    : "";
  const rawDescription = customDescription || fallbackDescription;
  const operatorDescription = normalizeRichTextHtml(rawDescription);
  const linkUrl = item.link?.url ?? null;
  const linkLabel = item.link?.label
    ? getLocalizedString(item.link.label, language)
    : null;
  const address = formatAddress(operator?.contact?.address);
  const imageId = item.featuredImageId ?? operator?.images?.[0]?._id ?? null;
  const imageUrl = getCloudinaryImageUrl(imageId, "f_auto,w_400,c_limit");

  const [lng, lat] = operator?.contact?.address?.location?.coordinates ?? [];
  const directionsLink =
    typeof lng === "number" && typeof lat === "number"
      ? `https://www.google.com/maps?q=${lat},${lng}`
      : null;
  const phone = operator?.contact?.phone ?? null;

  const businessType = operator?.type
    ? operator.type
        .replace(/_/g, " ")
        .replace(/business type/i, "")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const stopTag = item.overnight ? "Overnight" : businessType;
  const { html: truncatedDescription, isTruncated: isDescriptionTruncated } =
    truncateRichTextHtml(operatorDescription, DESCRIPTION_MAX_LENGTH);
  const canOpenInfoPanel = Boolean(operator && onNameClick);

  return (
    <div
      className={`tripper-item tripper-stop${isActive ? "" : " tripper-stop--inactive"}`}
      data-item-id={item._id}
    >
      {/* Two-column layout: badge/gutter column + tag/content column */}
      <div className="tripper-stop__columns">
        {/* Left column: badge + gutter line */}
        <div className="tripper-stop__left">
          <div className={`tripper-badge${isActive ? "" : " tripper-badge--inactive"}`}>
            {markerNumber}
          </div>
          <div className="tripper-stop__gutter">
            <div className="tripper-stop__gutter-line" />
          </div>
        </div>

        {/* Right column: tag (optional) + content */}
        <div className="tripper-stop__right">
          {!hideTag && stopTag && <span className="tripper-tag">{stopTag}</span>}

          <div className="tripper-stop__content">
            {/* Title + description */}
            {(tagline || operatorDescription) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {tagline && <p className="tripper-stop__tagline">{tagline}</p>}
                {operatorDescription && (
                  <div className="tripper-stop__description">
                    <div
                      className="tripper-stop__description-body"
                      dangerouslySetInnerHTML={{ __html: truncatedDescription }}
                    />
                    {isDescriptionTruncated && canOpenInfoPanel && (
                      <>
                        {" "}
                        <button
                          type="button"
                          className="tripper-stop__read-more"
                          onClick={() => {
                            if (operator) onNameClick?.(item, operator, markerNumber);
                          }}
                        >
                          {READ_MORE_LABEL[language] ?? READ_MORE_LABEL.en}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Custom link */}
            {linkUrl && (
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="tripper-stop__link-btn"
              >
                {linkLabel || "Learn more"}
              </a>
            )}

            {/* Image + business details */}
            <div className="tripper-stop__details">
              {(() => {
                const imageEl = imageUrl ? (
                  <img src={imageUrl} alt={operatorName ?? undefined} className="tripper-stop__image" />
                ) : (
                  <ImagePlaceholder borderRadius="8px" className="tripper-stop__image" />
                );
                return operator ? (
                  <button
                    type="button"
                    className="tripper-stop__image-button"
                    onClick={() => onNameClick?.(item, operator, markerNumber)}
                    aria-label={operatorName ? `View ${operatorName} details` : "View operator details"}
                  >
                    {imageEl}
                  </button>
                ) : (
                  imageEl
                );
              })()}

              <div className="tripper-stop__info">
                {operatorName && (
                  <button
                    type="button"
                    className="tripper-stop__biz-name"
                    onClick={() => onNameClick?.(item, operator, markerNumber)}
                  >
                    {operatorName}
                  </button>
                )}
                {address && <span className="tripper-stop__address">{address}</span>}
                <div className="tripper-stop__actions">
                  {phone && <span>{formatPhoneNumber(phone)}</span>}
                  {directionsLink && (
                    <a
                      href={directionsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => operatorEntityId && onOutboundClick?.(operatorEntityId, "google_maps")}
                    >
                      Google Maps
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
